import { TinyColor } from '@ctrl/tinycolor'
import createColorPalette, { type ColorPalette } from '../utils/createColorPalette.ts'

declare module 'styled-components' {
  export interface DefaultTheme {
    palette: ColorPalette
    primaryColor: string
    brandColor: string
    brandContrast: string
    brandContrastMuted: string
    overrideColor: string
    overrideContrast: string
    overrideContrastMuted: string
    main: string
    mainContrast: string
    mainContrastMuted: string
    bubbleIn: string
    bubbleInContrast: string
    bubbleInContrastMuted: string
    bubbleOut: string
    bubbleOutContrast: string
    bubbleOutContrastMuted: string
    buttonDarkBG: string
    buttonLightBG: string
    buttonDarkIcon: string
    buttonLightIcon: string
    buttonDarkHoverBG: string
    buttonDarkActiveBG: string
    buttonLightHoverBG: string
    buttonLightActiveBG: string
  }
}

const contrastColors = {
  black: '#3a3a3a',
  white: '#fafafa',
}

const contrastPlayerColors = {
  black: '#272F36',
  white: '#F7F9FA',
}

export const createTheme = (primaryColor = '') => {
  // brand (livechat)
  const brandColor = primaryColor || '#000000'
  const brandPalette = createColorPalette(brandColor)
  const isBrandLight = brandPalette.tinyColor.isLight()

  const brandContrast = isBrandLight ? contrastColors.black : contrastColors.white
  const brandContrastMuted = new TinyColor(brandContrast).setAlpha(0.7).toHexString()

  const overrideContrast = isBrandLight ? contrastColors.black : contrastColors.white
  const overrideContrastMuted = new TinyColor(overrideContrast).setAlpha(0.7).toHexString()

  // darken/lighten to bright/dark colors to make contrast colors work (only necessary for theme color)
  const luminance = brandPalette.tinyColor.getLuminance()
  let ThemeTC = brandPalette.tinyColor
  if (luminance < 0.1) {
    ThemeTC = brandPalette.tinyColor.lighten(4)
  } else if (luminance > 0.9) {
    ThemeTC = brandPalette.tinyColor.darken(4)
  }

  // default light button
  const buttonLight = new TinyColor('#F7F9FA')

  return {
    palette: brandPalette,
    primaryColor: brandColor,

    brandColor: primaryColor,
    brandContrast: brandContrast,
    brandContrastMuted: brandContrastMuted,

    overrideColor: brandColor,
    overrideContrast: overrideContrast,
    overrideContrastMuted: overrideContrastMuted,

    main: ThemeTC.toHexString(),
    mainContrast: overrideContrast,
    mainContrastMuted: overrideContrastMuted,

    bubbleIn: '#EBEEF0',
    bubbleInContrast: '#242424',
    bubbleInContrastMuted: '#626c73',

    bubbleOut: ThemeTC.toHexString(),
    bubbleOutContrast: overrideContrast,
    bubbleOutContrastMuted: overrideContrastMuted,

    bubbleLightPlayer: buttonLight.toHexString(),
    buttonLightBG: buttonLight.toHexString(),
    buttonLightHoverBG: buttonLight.darken(15).toHexString(),
    buttonLightActiveBG: buttonLight.darken(20).toHexString(),
    buttonLightIcon: '#1A2126',

    bubbleDarkPlayer: ThemeTC.toHexString(),
    buttonDarkBG: luminance > 0.9 ? ThemeTC.darken(5).toHexString() : ThemeTC.lighten(15).toHexString(),
    buttonDarkHoverBG: luminance > 0.9 ? ThemeTC.darken(10).toHexString() : ThemeTC.lighten(30).toHexString(),
    buttonDarkActiveBG: luminance > 0.9 ? ThemeTC.darken(15).toHexString() : ThemeTC.lighten(40).toHexString(),
    buttonDarkIcon: isBrandLight ? contrastPlayerColors.black : contrastPlayerColors.white,

    progressLightBG: '#FFFFFF',
    progressLightPassedBG: '#1A2126',
    overlayLightBG: '#F7F9FA',
  }
}
